(() => {
  const root = document.querySelector('[data-cart-shipping-calc]');
  if (!root) return;

  const els = {
    zip: root.querySelector('[data-cart-shipping-zip]'),
    country: root.querySelector('[data-cart-shipping-country]'),
    province: root.querySelector('[data-cart-shipping-province]'),
    submit: root.querySelector('[data-cart-shipping-submit]'),
    status: root.querySelector('[data-cart-shipping-status]'),
    results: root.querySelector('[data-cart-shipping-results]'),
    rates: root.querySelector('[data-cart-shipping-rates]'),
    summary: root.querySelector('[data-cart-shipping-summary]'),
    estTotal: root.querySelector('[data-cart-shipping-est-total]')
  };

  const storageKey = 'cart_shipping_calc_v3';
  const moneyFormat = (window.Shopify && window.Shopify.money_format) ? window.Shopify.money_format : '${{amount}}';
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  // Prevent older requests (eg WA) from overwriting newer ones (eg QLD)
  let activeRequestId = 0;

  function formatMoneyFromCents(cents) {
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
  }

  function setStatus(msg, type = 'info') {
    if (!els.status) return;
    els.status.textContent = msg || '';
    els.status.setAttribute('data-status', type);
  }

  function setLoading(isLoading) {
    if (!els.submit) return;
    els.submit.disabled = isLoading;
    els.submit.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function normalisePostcode(raw) {
    return String(raw || '').trim().replace(/\s+/g, '');
  }

  async function getCart() {
    const res = await fetch(rootPath + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error('Could not load cart.');
    return res.json();
  }

  function buildShippingRatesQuery(zip, country, province) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', zip);
    params.set('shipping_address[country]', country);

    // For AU, include these for best compatibility.
    params.set('shipping_address[country_code]', 'AU');

    if (province) params.set('shipping_address[province]', province);
    return params.toString();
  }

  async function fetchShippingRates(zip, country, province) {
    const query = buildShippingRatesQuery(zip, country, province);

    const res = await fetch(rootPath + 'cart/shipping_rates.json?' + query + '&t=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });

    // 202 means still calculating, not an error
    if (res.status === 202) return { status: 202 };

    // 422 means invalid address details
    if (res.status === 422) {
      let details = '';
      try { details = await res.text(); } catch (_) {}
      console.error('shipping_rates 422', details);
      return { status: 422, details };
    }

    if (!res.ok) {
      let details = '';
      try { details = await res.text(); } catch (_) {}
      console.error('shipping_rates failed', res.status, details);
      throw new Error('Could not fetch shipping rates. Please check the postcode and state, then try again.');
    }

    const data = await res.json();
    return { status: 200, data };
  }

  async function pollShippingRates(zip, country, province, requestId, { timeoutMs = 20000, intervalMs = 600 } = {}) {
    const start = Date.now();

    while (Date.now() - start < timeoutMs) {
      // If a newer request started, stop this one.
      if (requestId !== activeRequestId) return null;

      const r = await fetchShippingRates(zip, country, province);

      if (r && r.status === 200 && r.data && Array.isArray(r.data.shipping_rates)) {
        return r.data.shipping_rates;
      }

      if (r && r.status === 422) {
        throw new Error('Please check the postcode and state, then try again.');
      }

      // status 202, keep polling
      await new Promise(r2 => setTimeout(r2, intervalMs));
    }

    throw new Error('Shipping rates timed out. Please try again.');
  }

  function escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function addressKey(zip, country, province) {
    return [zip || '', country || '', province || ''].join('|');
  }

  function renderRates(rates, cartTotalCents, zip, country, province) {
    if (!els.rates) return;

    els.rates.innerHTML = '';
    if (els.results) els.results.hidden = false;
    if (els.summary) els.summary.hidden = true;

    if (!rates.length) {
      setStatus('No shipping rates found for that address.', 'warn');
      return;
    }

    setStatus('');

    rates.forEach((rate, idx) => {
      const priceCents = Math.round(parseFloat(rate.price) * 100);

      const li = document.createElement('li');
      li.className = 'cart-shipping-calc__rate';

      li.innerHTML = `
        <label class="cart-shipping-calc__rate-label">
          <input
            type="radio"
            name="cart_shipping_rate"
            value="${idx}"
            class="cart-shipping-calc__rate-radio"
            data-rate-price-cents="${priceCents}"
          >
          <span class="cart-shipping-calc__rate-name">${escapeHtml(rate.presentment_name || rate.name)}</span>
          <strong class="cart-shipping-calc__rate-price">${formatMoneyFromCents(priceCents)}</strong>
        </label>
      `;

      els.rates.appendChild(li);
    });

    els.rates.onchange = (e) => {
      const input = e.target.closest('input[type="radio"][name="cart_shipping_rate"]');
      if (!input) return;

      const shipCents = parseInt(input.getAttribute('data-rate-price-cents'), 10) || 0;
      const estCents = cartTotalCents + shipCents;

      if (els.estTotal) els.estTotal.textContent = formatMoneyFromCents(estCents);
      if (els.summary) els.summary.hidden = false;

      const payload = {
        addressKey: addressKey(zip, country, province),
        zip,
        country,
        province,
        selectedIndex: input.value
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    };

    // Restore selection only when it matches the exact address
    try {
      const savedRaw = localStorage.getItem(storageKey);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        const key = addressKey(zip, country, province);

        if (saved && saved.addressKey === key && typeof saved.selectedIndex !== 'undefined') {
          const radio = els.rates.querySelector(`input[value="${CSS.escape(String(saved.selectedIndex))}"]`);
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    } catch (_) {}
  }

  async function calculate() {
    const zip = normalisePostcode(els.zip && els.zip.value ? els.zip.value : '');
    const country = (els.country && els.country.value ? String(els.country.value).trim() : 'Australia');
    const province = (els.province && els.province.value ? String(els.province.value).trim() : '');

    if (!zip) {
      setStatus('Please enter a postcode.', 'warn');
      return;
    }

    if (!province) {
      setStatus('Please select a state.', 'warn');
      return;
    }

    // Start a new request, invalidating any in-flight poll
    activeRequestId += 1;
    const requestId = activeRequestId;

    try {
      setLoading(true);
      setStatus('Calculating shipping rates…', 'info');

      const cart = await getCart();

      const rates = await pollShippingRates(zip, country, province, requestId);
      if (!rates) return; // superseded by a newer request

      setStatus('');
      renderRates(rates, cart.total_price, zip, country, province);
    } catch (err) {
      console.error(err);
      setStatus((err && err.message) ? err.message : 'Something went wrong calculating shipping.', 'error');
      if (els.results) els.results.hidden = true;
    } finally {
      // Only clear loading if this is still the latest request
      if (requestId === activeRequestId) setLoading(false);
    }
  }

  if (els.submit) els.submit.addEventListener('click', calculate);

  if (els.zip) {
    els.zip.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        calculate();
      }
    });
  }

  // Restore saved inputs only (not rates)
  try {
    const savedRaw = localStorage.getItem(storageKey);
    if (savedRaw) {
      const s = JSON.parse(savedRaw);
      if (s && s.zip && els.zip) els.zip.value = s.zip;
      if (s && s.country && els.country) els.country.value = s.country;
      if (s && typeof s.province !== 'undefined' && els.province) els.province.value = s.province;
    }
  } catch (_) {}
})();
